import { animationFrameId, documentWithAnimationAtTime } from "@/document/animation/applyAnimation";
import { findSceneNode, type CurvaDocument } from "@/document/model/document";
import type { AnimationGraph } from "@/document/model/animation";
import type { FrameNode } from "@/document/model/nodes";
import { opaqueFrameFillClearColor } from "@/export/frameExportBackground";
import { documentForPngExport } from "@/export/pngExporter";
import type { Renderer } from "@/renderer/Renderer";
import type { Rect } from "@/lib/math/rect";
import { prepareWasmMeshKernels } from "@/renderer/scene/tessellation/curvaTessWasm";

export interface AnimationPreviewRenderOptions {
  document: CurvaDocument;
  animationGraph: AnimationGraph | undefined;
  activeAnimationRootId?: string;
  animationFrames: FrameNode[];
  timeMs: number;
  scale: number;
}

export interface AnimationPreviewRenderResult {
  frameId: string;
  bounds: Rect;
  sourceBounds: Rect;
}

export async function renderAnimationPreviewToCanvas(
  renderer: Renderer,
  options: AnimationPreviewRenderOptions,
): Promise<AnimationPreviewRenderResult | undefined> {
  await prepareWasmMeshKernels();
  const frameId = animationFrameId(options.animationGraph, options.activeAnimationRootId) ?? options.animationFrames[0]?.id;
  if (!options.animationGraph || !frameId) {
    return undefined;
  }

  const sourceFrame = findSceneNode(options.document, frameId);
  if (sourceFrame?.type !== "frame") {
    return undefined;
  }

  const evaluation = documentWithAnimationAtTime(options.document, options.timeMs, options.activeAnimationRootId);
  const animatedDocument = evaluation.document;
  const displayFrameId = evaluation.displayFrameId ?? frameId;
  const animatedFrame = findSceneNode(animatedDocument, displayFrameId);
  if (animatedFrame?.type !== "frame") {
    return undefined;
  }

  const exportDocument = documentForPngExport(animatedDocument, animatedFrame);
  await renderer.renderDocumentToCanvas(exportDocument, animatedFrame.bounds, Math.max(1, options.scale), {
    clearColor: opaqueFrameFillClearColor(sourceFrame),
  });

  return {
    frameId,
    bounds: { ...(evaluation.displayFrameBounds ?? sourceFrame.bounds) },
    sourceBounds: { ...sourceFrame.bounds },
  };
}
